import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { AdminRole, OrganizationRole } from '../types';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: AdminRole[];
  requiredOrgRoles?: OrganizationRole[];
  fallbackPath?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles = [],
  requiredOrgRoles = [],
  fallbackPath = '/login'
}) => {
  const { isAuthenticated, user, userRole, validateToken, logout } = useAuthStore();
  const location = useLocation();
  const [isValidating, setIsValidating] = useState(true);

  // Validate token on route change
  useEffect(() => {
    const checkAuthentication = async () => {
      if (isAuthenticated) {
        try {
          const isValid = await validateToken();
          if (!isValid) {
            logout();
          }
        } catch (error) {
          console.error('Token validation failed:', error);
          logout();
        }
      }
      setIsValidating(false);
    };

    checkAuthentication();
  }, [location.pathname, isAuthenticated, validateToken, logout]);

  // Show loading while validating
  if (isValidating) {
    return <div>Loading...</div>;
  }

  // If not authenticated, redirect to login with return path
  if (!isAuthenticated || !user) {
    return (
      <Navigate 
        to={fallbackPath} 
        state={{ from: location.pathname }} 
        replace 
      />
    );
  }

  // Check role-based permissions
  const hasRequiredRole = () => {
    console.log('Checking permissions:', { userRole, requiredRoles, requiredOrgRoles });
    
    if (requiredRoles.length === 0 && requiredOrgRoles.length === 0) {
      return true; // No specific roles required
    }

    // Check admin roles
    if (requiredRoles.length > 0 && userRole) {
      if (requiredRoles.includes(userRole)) {
        console.log('User has required admin role:', userRole);
        return true;
      }
    }

    // Check organization roles (if applicable)
    if (requiredOrgRoles.length > 0) {
      // For now, we'll implement basic org role checking
      // This can be extended based on user's organization context
      const userOrgRole = user?.organizationRole;
      if (userOrgRole && requiredOrgRoles.includes(userOrgRole)) {
        console.log('User has required org role:', userOrgRole);
        return true;
      }
    }

    console.log('User does not have required permissions');
    return false;
  };

  // If user doesn't have required permissions, redirect to unauthorized
  if (!hasRequiredRole()) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};